import type {ActionReducerMapBuilder} from '@reduxjs/toolkit';
import type {EventTicketingReducerState, FetchTicketingOrdersArgs, TicketingOrderSummary, TicketingOrdersBatch, TicketingOrdersListLazyData, TicketingStatusFilterValue} from '@Components/event-ticketing/event-ticketing.types';
import {getDefaultOrdersLazyData, getDefaultTicketingPlanLimit, getDefaultTicketingRsvpStats, getDefaultTicketingStats, TicketingErrorCode} from '@Components/event-ticketing/event-ticketing.types';
import {addTicketingAttendee, cancelTicketingAttendees, checkInTicketingAttendee, checkInTicketingOrder, checkOutTicketingAttendee, fetchFreeEventStats, fetchRsvpTicketingStats, fetchTicketingDashboard, fetchTicketingOrderSummaries, fetchTicketingOrders, fetchTicketingStats, markTicketOrderAsPaid, refundTicketOrder} from '@Components/event-ticketing/event-ticketing-thunk';
import {ALL_TICKETING_STATUS_FILTER_VALUES, getActiveOrdersList} from '@Libraries/event-ticketing.library';
import {LoadingStates} from '@Utils/loading.util';

const mergeOrdersIntoHashmap = (state: EventTicketingReducerState, orders: TicketingOrderSummary[]): void => {
  orders.forEach((order) => {
    state.ordersHashmap[order.orderId] = order;
  });
};

/** Appends the page's order ids, skipping any already present. Page 1 starts from an empty list (a filter change resets it). */
const appendOrdersPageToList = (list: TicketingOrdersListLazyData, batch: TicketingOrdersBatch): void => {
  const knownOrderIds = new Set(list.orderIds);
  const newOrderIds = batch.orders.map((order) => order.orderId).filter((orderId) => !knownOrderIds.has(orderId));
  list.orderIds = list.orderIds.concat(newOrderIds);
  list.currentPageNumber = batch.currentPageNumber;
  list.lastPage = batch.lastPage;
  list.total = batch.total;
  list.loadingState = LoadingStates.LOADED;
};

const areStatusFiltersEqual = (a: TicketingStatusFilterValue[], b: TicketingStatusFilterValue[]): boolean => {
  return a.length === b.length && a.every((value) => b.includes(value));
};

/**
 * True when an orders response no longer matches the live filters — the organiser changed the search or status
 * filter while it was in flight. Such a response is discarded so a stale set never lands in the current view.
 */
const isOrdersResponseOutdated = (state: EventTicketingReducerState, arg: FetchTicketingOrdersArgs): boolean => {
  return arg.search !== state.search || !areStatusFiltersEqual(arg.statuses, state.activeStatusFilters);
};

export const eventTicketingExtraReducers = (builder: ActionReducerMapBuilder<EventTicketingReducerState>): void => {
  builder.addCase(fetchTicketingDashboard.pending, (state) => {
    // Not cached per event, so each load starts clean — clears the previous event's orders and filters. The
    // default list is marked LOADING so the table's lazy-load doesn't also fetch page 1 (the response seeds it).
    state.loadingState = LoadingStates.LOADING;
    state.statsLoadingState = LoadingStates.LOADING;
    state.stats = getDefaultTicketingStats();
    state.rsvpStats = getDefaultTicketingRsvpStats();
    state.freeStats = getDefaultTicketingRsvpStats();
    state.hasFetchEventFailed = false;
    state.isTicketingUnavailableForEvent = false;
    state.ordersHashmap = {};
    state.defaultOrders = getDefaultOrdersLazyData();
    state.defaultOrders.loadingState = LoadingStates.LOADING;
    state.filteredOrders = getDefaultOrdersLazyData();
    state.search = '';
    state.liveSearchTerm = '';
    state.activeStatusFilters = [...ALL_TICKETING_STATUS_FILTER_VALUES];
    state.planLimit = getDefaultTicketingPlanLimit();
  });

  builder.addCase(fetchTicketingDashboard.fulfilled, (state, {payload}) => {
    state.event = payload.event;
    // The wire response sends exactly one of these (the other is null for the event's mode); coalesce the inactive
    // one to its zeroed default so no store field is ever null and the row always has data to show.
    state.planLimit = payload.planLimit;
    state.stats = payload.stats ?? getDefaultTicketingStats();
    state.rsvpStats = payload.rsvpStats ?? getDefaultTicketingRsvpStats();
    state.freeStats = payload.freeStats ?? getDefaultTicketingRsvpStats();
    mergeOrdersIntoHashmap(state, payload.orders.orders);
    appendOrdersPageToList(state.defaultOrders, payload.orders);
    state.loadingState = LoadingStates.LOADED;
    state.statsLoadingState = LoadingStates.LOADED;
    state.hasFetchEventFailed = false;
    state.isTicketingUnavailableForEvent = false;
  });

  builder.addCase(fetchTicketingDashboard.rejected, (state, {meta, payload}) => {
    if (meta.aborted) {
      return;
    }
    state.loadingState = LoadingStates.NOT_LOADED;
    state.statsLoadingState = LoadingStates.NOT_LOADED;
    state.defaultOrders.loadingState = LoadingStates.NOT_LOADED;
    state.hasFetchEventFailed = true;
    // The backend refused because paid ticketing is off for this viewer — the page redirects out on this rather
    // than leaving them on the retry message, since retrying can never succeed.
    state.isTicketingUnavailableForEvent = payload?.code === TicketingErrorCode.TICKETING_FEATURE_UNAVAILABLE;
  });

  builder.addCase(fetchTicketingOrders.pending, (state, {meta}) => {
    getActiveOrdersList(state, meta.arg.search, meta.arg.statuses).loadingState = LoadingStates.LOADING;
  });

  builder.addCase(fetchTicketingOrders.fulfilled, (state, {meta, payload}) => {
    if (isOrdersResponseOutdated(state, meta.arg)) {
      return;
    }
    mergeOrdersIntoHashmap(state, payload.orders);
    appendOrdersPageToList(getActiveOrdersList(state, meta.arg.search, meta.arg.statuses), payload);
  });

  builder.addCase(fetchTicketingOrders.rejected, (state, {meta}) => {
    if (meta.aborted || isOrdersResponseOutdated(state, meta.arg)) {
      return;
    }
    getActiveOrdersList(state, meta.arg.search, meta.arg.statuses).loadingState = LoadingStates.LOADED;
  });

  // Order-mutating actions return the order's fresh row summary; merging it updates the table row in place (the
  // modal manages its own detail state locally).
  // Rows render from the hashmap, so merging the re-read summaries updates them in place — the lists keep their
  // ids, their paging, and the organiser's search and status filter.
  builder.addCase(fetchTicketingOrderSummaries.fulfilled, (state, {payload}) => {
    mergeOrdersIntoHashmap(state, payload);
  });

  builder.addCase(checkInTicketingAttendee.fulfilled, (state, {payload}) => {
    mergeOrdersIntoHashmap(state, [payload]);
  });

  builder.addCase(checkInTicketingOrder.fulfilled, (state, {payload}) => {
    mergeOrdersIntoHashmap(state, [payload]);
  });

  builder.addCase(checkOutTicketingAttendee.fulfilled, (state, {payload}) => {
    mergeOrdersIntoHashmap(state, [payload]);
  });

  builder.addCase(markTicketOrderAsPaid.fulfilled, (state, {payload}) => {
    mergeOrdersIntoHashmap(state, [payload]);
  });

  // Cancelling tickets returns no money and doesn't change tickets-sold, so unlike refund it needs no stats refresh.
  builder.addCase(cancelTicketingAttendees.fulfilled, (state, {payload}) => {
    mergeOrdersIntoHashmap(state, [payload]);
  });

  // A refund returns the order's updated row summary (plus a new refundable amount the refund modal reads from its
  // own unwrapped result); merging the summary updates the row's status chips in place.
  builder.addCase(refundTicketOrder.fulfilled, (state, {payload}) => {
    mergeOrdersIntoHashmap(state, [payload.order]);
  });

  // A hand-added attendee creates a NEW order: store its row at the top of the cached unfiltered list. It's added
  // to the default list only (a new order can't be known to match an active filter) — it surfaces once cleared.
  builder.addCase(addTicketingAttendee.fulfilled, (state, {payload}) => {
    const isNewOrder = state.ordersHashmap[payload.orderId] === undefined;
    state.ordersHashmap[payload.orderId] = payload;
    state.defaultOrders.orderIds = [payload.orderId, ...state.defaultOrders.orderIds.filter((orderId) => orderId !== payload.orderId)];
    if (isNewOrder) {
      state.defaultOrders.total += 1;
    }
  });

  // Targeted stats refresh after a refund / mark-as-paid: swap the row's figures in place, no loading state.
  builder.addCase(fetchTicketingStats.fulfilled, (state, {payload}) => {
    state.stats = payload;
  });

  // The RSVP counterpart: targeted refresh after an RSVP add-attendee / cancel, swapped in place with no loading state.
  builder.addCase(fetchRsvpTicketingStats.fulfilled, (state, {payload}) => {
    state.rsvpStats = payload;
  });

  // The free-event counterpart: targeted refresh after a free-event add-attendee / cancel, swapped in place.
  builder.addCase(fetchFreeEventStats.fulfilled, (state, {payload}) => {
    state.freeStats = payload;
  });
};
